import React, { useEffect, useState } from "react";
import AppShell from "@/components/ascend/AppShell";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AESTHETIC_PRESETS } from "@/lib/themes";
import { Check, Milestone, Palette, Save } from "lucide-react";
import { toast, Toaster } from "sonner";

const NICE_COLORS = [
  "#7BAFD4", "#C99DB3", "#E5A76E", "#8EAE7B",
  "#B6A0C6", "#7CB5A3", "#D68C8C", "#6D8BC2",
  "#C0A870", "#9C8BB0",
];

export default function Subjects() {
  const { user, patchLocalUser } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [savingAesthetic, setSavingAesthetic] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/subjects");
        setSubjects(data.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const update = (id, patch) => setSubjects((arr) => arr.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const save = async (subject) => {
    setSavingId(subject.id);
    try {
      const body = { name: subject.name, color: subject.color };
      if (!subject.is_milestone_based) body.weekly_hour_target = subject.weekly_hour_target ?? 0;
      const { data } = await api.patch(`/subjects/${subject.id}`, body);
      update(subject.id, data);
      toast.success(`${data.name} saved.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  const changeAesthetic = async (preset) => {
    setSavingAesthetic(true);
    try {
      const { data } = await api.patch("/me/aesthetic", { aesthetic_preset: preset });
      patchLocalUser({ aesthetic_preset: data.aesthetic_preset });
      document.documentElement.setAttribute("data-theme", data.aesthetic_preset);
      toast.success("Vibe updated.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingAesthetic(false);
    }
  };

  return (
    <AppShell>
      <Toaster richColors position="top-center" />
      <div className="pt-2">
        <div className="mb-6 fade-up">
          <span className="eyebrow">your study space</span>
          <h1 className="font-display text-4xl sm:text-5xl mt-1" style={{ color: "var(--text)" }}>
            Subjects
          </h1>
          <p className="text-muted mt-1">Rename, recolour, and set weekly hour targets. EPQ tracks milestones instead of hours.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="card-soft p-6 h-52 animate-pulse" />
              ))
            : subjects.map((s, i) => (
                <div
                  key={s.id}
                  className={`card-soft p-6 fade-up delay-${Math.min(i + 1, 4)}`}
                  data-testid={`subject-editor-${s.name.toLowerCase()}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span
                        className="inline-block w-3.5 h-3.5 rounded-full"
                        style={{ background: s.color, boxShadow: `0 0 0 4px ${s.color}25` }}
                      />
                      <input
                        className="input-soft font-display text-xl px-3 py-2"
                        value={s.name}
                        onChange={(e) => update(s.id, { name: e.target.value })}
                        data-testid={`subject-name-${i}`}
                      />
                    </div>
                    {s.is_milestone_based && (
                      <span
                        className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-full flex items-center gap-1"
                        style={{ background: "var(--surface-2)", color: "var(--accent)", border: "1px solid var(--border)" }}
                      >
                        <Milestone size={12} strokeWidth={1.5} /> milestone
                      </span>
                    )}
                  </div>

                  {!s.is_milestone_based && (
                    <div className="mb-4">
                      <label className="text-xs uppercase tracking-wider text-muted mb-1 block">Weekly hour target</label>
                      <input
                        type="number"
                        min={0}
                        max={168}
                        className="input-soft w-32"
                        value={s.weekly_hour_target ?? 0}
                        onChange={(e) => update(s.id, { weekly_hour_target: Number(e.target.value) })}
                        data-testid={`subject-hours-${i}`}
                      />
                    </div>
                  )}

                  <div>
                    <label className="text-xs uppercase tracking-wider text-muted mb-2 block">Colour</label>
                    <div className="flex flex-wrap gap-2">
                      {NICE_COLORS.map((c) => (
                        <button
                          key={c}
                          onClick={() => update(s.id, { color: c })}
                          className="w-7 h-7 rounded-full border-2 transition-transform hover:scale-110"
                          style={{
                            background: c,
                            borderColor: s.color === c ? "var(--text)" : "transparent",
                          }}
                          data-testid={`subject-color-${i}-${c}`}
                          aria-label={`Set colour ${c}`}
                        >
                          {s.color === c && <Check size={14} strokeWidth={2.5} color="#fff" className="mx-auto" />}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-5 flex justify-end">
                    <button
                      onClick={() => save(s)}
                      disabled={savingId === s.id}
                      className="btn-primary flex items-center gap-2 text-sm disabled:opacity-60"
                      data-testid={`subject-save-${i}`}
                    >
                      <Save size={14} strokeWidth={1.75} />
                      {savingId === s.id ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
              ))}
        </div>

        {/* Aesthetic switcher */}
        <div className="fade-up">
          <div className="flex items-center gap-2 mb-2">
            <Palette size={16} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
            <span className="eyebrow">the whole feel</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl mb-4" style={{ color: "var(--text)" }}>Change the vibe</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {AESTHETIC_PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => changeAesthetic(p.id)}
                disabled={savingAesthetic}
                data-selected={user?.aesthetic_preset === p.id}
                className="preset-chip text-left"
                data-testid={`aesthetic-switch-${p.id}`}
                style={{ background: p.gradient, color: p.text }}
              >
                <div className="font-display text-2xl leading-tight">{p.name}</div>
                <div className="text-xs mt-1 opacity-80">{p.tagline}</div>
                <div className="flex gap-1.5 mt-4">
                  {p.swatches.map((c) => (
                    <span key={c} className="w-5 h-5 rounded-full border border-white/40" style={{ background: c }} />
                  ))}
                </div>
                {user?.aesthetic_preset === p.id && (
                  <span
                    className="absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ background: p.text, color: "#fff" }}
                  >
                    <Check size={14} strokeWidth={2} />
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
